import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Kicker, Tag } from "@/components/Reveal";

/* ============================================================
   EDITABLE CONTENT — Cultural Governance Committee
   Update the text below as wording is confirmed with the
   committee. Members render only when `published: true`, and
   empty fields are hidden automatically.
   TO PUBLISH: remove the sample profile below, set
   COMMITTEE_ROLE.confirmed to true once wording is agreed,
   then remove `noindex` from this page's SEO_MAP entry in
   App.js, remove the NODE_ENV gates around the route in
   App.js, the nav/footer links in Layout.jsx and the
   section in Aunties.jsx, and add the page to
   public/sitemap.xml + SearchOverlay.
   ============================================================ */

const INTRO = [
  "Ngawilyi Narrative is establishing a Cultural Governance Committee to bring cultural guidance, community perspectives and shared reflection into the organisation's development.",
  "The committee will help inform our launch, services and ongoing direction, keeping culture, relationships and community priorities central as Ngawilyi grows.",
];

// DRAFT — replace with the committee's own agreed wording once confirmed.
const COMMITTEE_ROLE = {
  confirmed: false,
  text: "This space will hold a short description of the committee's agreed role, how and when it meets, and the way its guidance informs Ngawilyi's decisions — written in the committee's own words once confirmed.",
};

const COMMITTEE_MEMBERS = [
  {
    name: "Sample Member Name",
    title: "Sample title — e.g. Aunty, Uncle, Community Member",
    connections: "Sample Nation / community connections — each member's own approved wording will appear here.",
    role: "Sample committee role — shown only where confirmed",
    photo: "",
    bio: "Sample biography text. A short biography written with each member, in their own words, will appear here.",
    experience: "Sample text describing the experience, knowledge and perspectives the member wishes to share.",
    published: true,
    sample: true,
  },
];

const MemberCard = ({ m, i }) => (
  <Reveal delay={i * 0.08} className="h-full">
    <article data-testid={`cg-member-${i}`} className="rounded-[36px] bg-creamdark border border-ochre/25 p-8 sm:p-10 h-full">
      {m.sample && (
        <p data-testid={`cg-member-${i}-sample-badge`} className="mb-6 inline-block rounded-full bg-sunset/15 border border-sunset/40 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-terracotta">
          Sample profile — dummy content, preview only
        </p>
      )}
      {m.photo && (
        <div className="organic-blob overflow-hidden aspect-[5/4] grain relative mb-7 bg-gradient-to-br from-terracotta via-sunset to-ochre">
          <img src={m.photo} alt={`Portrait of ${m.name}`} className="absolute inset-0 h-full w-full object-cover" />
        </div>
      )}
      <h3 className="font-display text-3xl text-charcoal">{m.name}</h3>
      {m.title && <p className="mt-2 text-xs uppercase tracking-[0.22em] font-bold text-terracotta">{m.title}</p>}
      {m.role && <div className="mt-5"><Tag tone="terracotta">{m.role}</Tag></div>}
      {m.connections && <p className="mt-5 font-display italic text-lg text-earth/90 leading-relaxed">{m.connections}</p>}
      {m.bio && <p className="mt-4 text-[15px] leading-relaxed text-earth/85">{m.bio}</p>}
      {m.experience && (
        <div className="mt-6 border-t border-ochre/25 pt-5">
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-earth/60 mb-2">Experience, knowledge & perspectives</p>
          <p className="text-[15px] leading-relaxed text-earth/85">{m.experience}</p>
        </div>
      )}
    </article>
  </Reveal>
);

const CulturalGovernance = () => {
  const members = COMMITTEE_MEMBERS.filter((m) => m.published);
  return (
    <div data-testid="cultural-governance-page">
      <PageHero
        kicker="Cultural Governance"
        title={"Culture guiding\nthe way we grow."}
        lede="An emerging circle of cultural guidance, community perspectives and shared reflection at the heart of Ngawilyi Narrative."
      >
        <div className="mt-8"><Tag tone="ochre">Being established — shaped through community consultation</Tag></div>
      </PageHero>

      {/* INTRODUCTION */}
      <section className="px-6 py-16 sm:py-24 bg-cream">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <Kicker>Why a committee</Kicker>
            <div data-testid="cg-intro" className="mt-7 space-y-5">
              {INTRO.map((p) => (
                <p key={p.slice(0, 24)} className="text-base lg:text-lg leading-relaxed text-earth">{p}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* THE COMMITTEE'S ROLE */}
      <section className="px-6 pb-16 sm:pb-24 bg-cream">
        <Reveal>
          <div data-testid="cg-role" className="max-w-3xl mx-auto rounded-[40px] bg-blush/40 border border-blush p-9 sm:p-12">
            <div className="flex flex-wrap items-center gap-4 mb-5">
              <Tag tone="terracotta">{COMMITTEE_ROLE.confirmed ? "Confirmed by the committee" : "Draft — awaiting committee confirmation"}</Tag>
            </div>
            <h2 className="font-display font-light text-3xl sm:text-4xl leading-snug text-charcoal">The committee's role</h2>
            <p className="mt-5 text-earth/85 leading-relaxed">{COMMITTEE_ROLE.text}</p>
          </div>
        </Reveal>
      </section>

      {/* MEMBERS */}
      <section className="px-6 py-16 sm:py-24 bg-cream border-t border-ochre/15">
        <div className="max-w-6xl mx-auto">
          <Reveal className="mb-12">
            <Kicker>Committee members</Kicker>
            <h2 className="mt-4 font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal max-w-2xl">
              Introduced in <span className="italic text-terracotta">their own words.</span>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-earth/80 max-w-2xl">
              Profiles are added gradually, as members confirm how they wish to be introduced. Each member's
              Nation and community connections are written in their own approved wording.
            </p>
          </Reveal>
          {members.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-6">
              {members.map((m, i) => <MemberCard key={m.name} m={m} i={i} />)}
            </div>
          ) : (
            <Reveal>
              <p data-testid="cg-members-empty" className="rounded-[36px] bg-creamdark border border-ochre/25 p-9 text-earth/80 leading-relaxed max-w-2xl">
                Member profiles will appear here as the committee is formed and each member confirms how they
                wish to be introduced.
              </p>
            </Reveal>
          )}
        </div>
      </section>

      {/* CLOSING */}
      <section className="px-6 pb-20 sm:pb-28 bg-cream">
        <Reveal>
          <div className="max-w-3xl mx-auto">
            <p className="font-display italic text-2xl sm:text-3xl text-charcoal leading-snug">
              Culture, relationships and community priorities — central as Ngawilyi grows.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link data-testid="cg-aunties-link" to="/aunties" className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105">
                Meet Our Aunties <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link data-testid="cg-contact-link" to="/contact" className="inline-flex items-center gap-2 rounded-full border-2 border-charcoal px-8 py-[14px] text-sm font-bold uppercase tracking-widest text-charcoal transition-colors duration-300 hover:bg-charcoal hover:text-cream">
                Yarn With Us
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default CulturalGovernance;
